import { Box, Input, InputGroup, type InputProps } from '@chakra-ui/react';
import type React from 'react';
import { MdError } from 'react-icons/md';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { colors } from '@/components/ui/theme';
import { Tooltip } from '@/components/ui/tooltip';

export interface FaceliftInputProps extends InputProps {
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	sx?: React.CSSProperties;
	inputKey?: string;
	bg?: string;
	color?: string;
	topLabel?: string;
	error?: string;
	required?: boolean;
	bordered?: boolean;
	startIcon?: React.ReactNode;
	showIconWithError?: boolean;
	endIcon?: React.ReactNode;
}

const FaceliftInput: React.FC<FaceliftInputProps> = ({
	onChange,
	sx,
	inputKey,
	topLabel,
	required,
	bg = 'brand.inputBg',
	color = 'brand.inputText',
	startIcon,
	endIcon,
	error,
	bordered = false,
	size = 'xs',
	showIconWithError = false,
	...rest
}) => {
	return (
		<Box>
			{topLabel && (
				<Box display="flex" mb={1} gap={1}>
					<FaceliftText fontStyle="italic" fontSize="sm">{topLabel}</FaceliftText>
					<FaceliftText color="red.500">{required ? '*' : ''}</FaceliftText>
				</Box>
			)}

			<Box position="relative">
				<Tooltip
					showArrow
					contentProps={{ css: { '--tooltip-bg': 'colors.brand.errors', display: error ? 'block' : 'none' } }}
					color="brand.muted"
					open={!!error}
					content={error}
					positioning={{ placement: 'top-center' }}
					portalled={false}
				>
					<InputGroup
						bg={bg}
						startElement={startIcon}
						endElement={
							error ? <Box gap={1} display="flex" alignItems="center"><MdError size="16" color={colors.errors} />{showIconWithError && endIcon}</Box> : endIcon
						}
						border={bordered ? '1px solid' : 'none'}
						borderRadius={bordered ? 'full' : 'none'}
					>
						<Input
							bg={bg}
							color={color}
							_placeholder={{ color: 'brand.inputPlaceholder' }}
							key={inputKey}
							onChange={onChange}
							display="inline-flex"
							alignItems="center"
							outline={0}
							border="none"
							boxShadow="none"
							variant="flushed"
							size={size}
							borderBottom={bordered ? '' : '2px solid'}
							borderImage={
								!error
									? 'linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%) 1'
									: ''
							}
							borderColor={!error ? '' : 'brand.errors'}
							borderRadius={bordered ? '20px' : 'none'}
							px={3}
							style={sx}
							{...rest}
						/>
					</InputGroup>
				</Tooltip>
			</Box>
		</Box>
	);
};

export default FaceliftInput;
